import { useMemo } from "react";
import { useInfiniteQuery, useQuery } from "react-query";
import axios from "axios";
import { useApi } from "./useApi";
import { useActiveGithubOrgForUser } from "./useConnectedGithubOrgs";
import * as z from "zod";
import { LogLevel } from "@/components/LogTable";
import { recentEndDateAtom } from "@/atoms/runsFilterAtoms";
import { useAtom } from "jotai";
import { recentStartDateAtom } from "@/atoms/runsFilterAtoms";
import { type NamedOrg, orgApiPath } from "@/lib/named-org";

const LogEntry = z.object({
  installation_id: z.string(),
  record_id: z.string(),
  record_name: z.string(),
  record_type: z.string(),
  record_order: z.number(),
  job_id: z.string().nullable(),
  job_name: z.string().nullable(),
  job_display_name: z.string().nullable(),
  repo: z.string(),
  workflow: z.string(),
  branch: z.string(),
  pull_request_number: z
    .number()
    .transform((val) => (val === 0 ? null : val))
    .nullable(),
  run_id: z.string(),
  run_attempt: z.number(),
  vm_id: z.string(),
  sender: z.string(),
  log_timestamp: z.string().transform((val) => {
    const date = new Date(val);
    // Ensure we parse as UTC by appending Z if not already present
    if (!val.endsWith("Z")) {
      return new Date(val + "Z");
    }
    return date;
  }),
  message: z.string(),
  log_line_number: z.number().transform((x) => x ?? 0),
  level: z
    .string()
    .optional()
    .transform((val) => val ?? "info") as z.ZodType<LogLevel>,
});

export type LogEntry = z.infer<typeof LogEntry>;

export type LogCursor = {
  log_timestamp: string;
  log_line_number: number;
  record_id: string;
};

const LogSearchResponse = z.object({
  logs: z.array(LogEntry),
  next_cursor: z
    .object({
      log_timestamp: z.string(),
      log_line_number: z.number().transform((x) => x ?? 0),
      record_id: z.string(),
    })
    .nullable(),
  has_more_logs: z.boolean().optional(),
});

export type LogSearchResponse = z.infer<typeof LogSearchResponse>;

/** Paginated log search over the active org and the selected date range. */
export function useLogSearch(queryString: string) {
  const api = useApi();
  const { activeNamedOrg } = useActiveGithubOrgForUser();
  const [startDate] = useAtom(recentStartDateAtom);
  const [endDate] = useAtom(recentEndDateAtom);

  const baseQueryKey = useMemo(
    () => [
      "logSearch",
      activeNamedOrg,
      startDate.toISOString(),
      endDate.toISOString(),
      queryString,
    ],
    [activeNamedOrg, startDate, endDate, queryString],
  );

  const result = useInfiniteQuery(
    baseQueryKey,
    ({ pageParam }) => {
      if (!activeNamedOrg) throw new Error("Active organization is required");

      return fetchLogSearch(
        api,
        activeNamedOrg,
        { from: startDate, to: endDate },
        queryString,
        pageParam,
      );
    },
    {
      enabled: !!activeNamedOrg,
      staleTime: 5 * 60 * 1000,
      getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    },
  );
  const logs = useMemo(
    () => result.data?.pages.flatMap((page) => page.logs) ?? [],
    [result.data],
  );

  return {
    ...result,
    data: logs,
    hasNextPage: Boolean(
      result.hasNextPage &&
      !result.isError &&
      (!result.isFetching || result.isFetchingNextPage),
    ),
  };
}

export async function fetchLogSearch(
  api: ReturnType<typeof useApi>,
  org: NamedOrg,
  dateRange: { from: Date; to: Date },
  query: string,
  cursor?: LogCursor,
) {
  const params = new URLSearchParams();

  params.append("start_time", dateRange.from.toISOString());
  params.append("end_time", dateRange.to.toISOString());
  params.append("query", query);

  if (cursor) {
    params.append("cursor_timestamp", cursor.log_timestamp);
    params.append("cursor_record_id", cursor.record_id);
    params.append("cursor_log_line_number", cursor.log_line_number.toString());
  }

  const response = await api.get(
    `${orgApiPath(org)}/metrics/logs/search?${params.toString()}`,
  );

  return LogSearchResponse.parse(response.data);
}

const FilterOption = z.object({
  value: z.string(),
  present: z.boolean(),
});

const FilterOptions = z.object({
  branches: z.array(FilterOption).optional(),
  senders: z.array(FilterOption).optional(),
  repos: z.array(FilterOption).optional(),
  workflows: z.array(FilterOption).optional(),
  levels: z
    .array(FilterOption)
    .optional()
    .transform((x) => x ?? []),
  job_names: z.array(FilterOption).optional(),
  step_names: z.array(FilterOption).optional(),
});

export type LogSearchFilterOptionsProperty =
  | "workflows"
  | "job_names"
  | "step_names"
  | "branches"
  | "senders"
  | "repos";

const FILTER_OPTIONS_PROPERTIES: LogSearchFilterOptionsProperty[] = [
  "repos",
  "workflows",
  "job_names",
  "step_names",
  "branches",
  "senders",
];

export function useLogSearchFilterOptions(enabled: boolean = true) {
  const api = useApi();
  const { activeNamedOrg } = useActiveGithubOrgForUser();

  const [startDate] = useAtom(recentStartDateAtom);
  const [endDate] = useAtom(recentEndDateAtom);

  const optionsQueryKey = useMemo(
    () => ["logSearchFilterOptions", activeNamedOrg, startDate, endDate],
    [activeNamedOrg, startDate, endDate],
  );

  const queryResult = useQuery(
    optionsQueryKey,
    async () => {
      if (!activeNamedOrg) {
        return {
          levels: [],
          branches: [],
          senders: [],
          repos: [],
          workflows: [],
          job_names: [],
          step_names: [],
        };
      }

      return await fetchLogSearchFilterOptions(
        api,
        activeNamedOrg,
        {
          from: startDate,
          to: endDate,
        },
        FILTER_OPTIONS_PROPERTIES,
      );
    },
    {
      enabled: !!activeNamedOrg && enabled,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );

  return queryResult;
}

export async function fetchLogSearchFilterOptions(
  api: ReturnType<typeof useApi>,
  org: NamedOrg,
  dateRange: { from: Date; to: Date },
  properties: LogSearchFilterOptionsProperty[],
) {
  const url = `${orgApiPath(org)}/metrics/logs/search/filter-options`;
  const baseParams = {
    start_time: dateRange.from.toISOString(),
    end_time: dateRange.to.toISOString(),
  };
  const params = new URLSearchParams(baseParams);
  properties.forEach((property) => params.append("properties[]", property));

  try {
    const response = await api.get(`${url}?${params.toString()}`);
    return FilterOptions.parse(response.data);
  } catch (error) {
    // The dashboard can deploy ahead of the backend, whose previous release
    // rejects properties[] with a 422 and only accepts a single property.
    if (!axios.isAxiosError(error) || error.response?.status !== 422) {
      throw error;
    }
    const responses = await Promise.all(
      properties.map((property) =>
        api.get(
          `${url}?${new URLSearchParams({
            ...baseParams,
            property,
          }).toString()}`,
        ),
      ),
    );
    return FilterOptions.parse(
      Object.assign({}, ...responses.map((response) => response.data)),
    );
  }
}

const Histogram = z.object({
  buckets: z.array(
    z.object({
      start: z.string(),
      end: z.string(),
      log_count: z.number(),
      error_count: z.number(),
      warn_count: z.number(),
      info_count: z.number(),
      debug_count: z.number(),
    }),
  ),
  meta: z.object({
    total_count: z.number(),
  }),
});

export async function fetchLogSearchHistogram(
  api: ReturnType<typeof useApi>,
  org: NamedOrg,
  query: string,
  dateRange: { from: Date; to: Date },
) {
  const response = await api.get(
    `${orgApiPath(org)}/metrics/logs/histogram?query=${encodeURIComponent(
      query,
    )}&start_time=${dateRange.from.toISOString()}&end_time=${dateRange.to.toISOString()}`,
  );

  return Histogram.parse(response.data);
}

export const useLogSearchHistogram = (query: string) => {
  const api = useApi();
  const { activeNamedOrg } = useActiveGithubOrgForUser();
  const [startDate] = useAtom(recentStartDateAtom);
  const [endDate] = useAtom(recentEndDateAtom);

  const queryKey = useMemo(
    () => [
      "logSearchHistogram",
      activeNamedOrg,
      query,
      startDate.toISOString(),
      endDate.toISOString(),
    ],
    [activeNamedOrg, query, startDate, endDate],
  );

  return useQuery(
    queryKey,
    async () => {
      if (!activeNamedOrg) return { buckets: [], meta: { total_count: 0 } };

      const response = await fetchLogSearchHistogram(
        api,
        activeNamedOrg,
        query,
        { from: startDate, to: endDate },
      );

      return response;
    },
    {
      suspense: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );
};
